// Screen navigation constants
export const SCREENS = {
  WELCOME: 'WELCOME',
  ORIENTATION_SELECTION: 'ORIENTATION_SELECTION',
  CAMERA: 'CAMERA',
  STYLE_SELECTION: 'STYLE_SELECTION',
  LOADING: 'LOADING',
  RESULT: 'RESULT',
  QR_CODE: 'QR_CODE',
  IMAGE_VIEWER: 'IMAGE_VIEWER'
};

// Environment backgrounds for "Environment Only" mode
export const ENVIRONMENT_STYLES = {
  SPACE: {
    id: 'space',
    name: 'Space',
    description: 'Cosmic galaxy adventure',
    prompt: 'a cosmic deep space environment with vibrant purple and blue nebula clouds, countless twinkling stars, distant galaxies with spiral arms, cosmic dust particles, ethereal glowing auroras, planet surfaces or moons visible in the distance, dramatic rim lighting from distant stars, mysterious and awe-inspiring atmosphere, ultra-realistic space photography style with rich colors and deep blacks',
    previewImage: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=400&h=600&fit=crop',
    gradient: 'from-indigo-600 to-purple-600',
    emoji: '🚀'
  },
  BEACH: {
    id: 'beach',
    name: 'Beach',
    description: 'Tropical paradise sunset',
    prompt: 'a stunning tropical beach paradise with fine golden sand, crystal-clear turquoise ocean water with gentle waves, tall swaying palm trees with green fronds, warm golden-hour sunset lighting with orange and pink sky, soft clouds, distant horizon line, tropical plants and flowers, peaceful and serene atmosphere, professional travel photography style with vibrant warm colors and soft natural lighting',
    previewImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=600&fit=crop',
    gradient: 'from-cyan-600 to-blue-600',
    emoji: '🏖️'
  },
  DESERT: {
    id: 'desert',
    name: 'Desert',
    description: 'Arabian desert landscape',
    prompt: 'a majestic Arabian desert landscape with smooth golden sand dunes creating flowing curves and ridges, warm amber and orange sunset lighting casting long dramatic shadows, clear blue to orange gradient sky, occasional desert plants or sparse vegetation, distant mountains on the horizon, heat shimmer effects, exotic and adventurous atmosphere, professional landscape photography with rich warm tones and high contrast',
    previewImage: 'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=400&h=600&fit=crop',
    gradient: 'from-orange-600 to-red-600',
    emoji: '🏜️'
  },
  CYBERPUNK: {
    id: 'cyberpunk',
    name: 'Cyberpunk City',
    description: 'Neon-lit futuristic cityscape',
    prompt: 'a futuristic cyberpunk city environment with towering skyscrapers covered in glowing neon signs (pink, cyan, purple colors), holographic advertisements floating in the air, wet reflective streets with puddles, rain and atmospheric fog, flying vehicles or drones, dense urban architecture, metallic and glass surfaces, moody blue and purple ambient lighting, dystopian high-tech atmosphere, cinematic blade runner style with strong color contrast and dramatic lighting',
    previewImage: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=600&fit=crop',
    gradient: 'from-purple-600 to-blue-600',
    emoji: '🌃'
  },
  FOREST: {
    id: 'forest',
    name: 'Forest',
    description: 'Mystical woodland scene',
    prompt: 'an enchanted mystical forest environment with towering ancient trees covered in moss and vines, lush vibrant green foliage and ferns on the forest floor, soft magical rays of light filtering through the canopy creating god rays, floating particles of pollen or fireflies, colorful wildflowers, mushrooms, misty atmosphere, rich emerald and jade green colors, peaceful and mysterious mood, fantasy forest photography style with dreamy lighting',
    previewImage: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=400&h=600&fit=crop',
    gradient: 'from-green-600 to-emerald-600',
    emoji: '🌲'
  },
  MOUNTAIN: {
    id: 'mountain',
    name: 'Mountains',
    description: 'Majestic mountain peaks',
    prompt: 'a majestic mountain landscape with towering snow-capped peaks, rugged rocky cliffs and ridges, alpine meadows with wildflowers, clear blue sky with wispy clouds, dramatic golden hour or sunrise lighting creating warm glows on mountain faces, pine trees in foreground, alpine lakes or waterfalls, crisp clean air atmosphere, epic and inspiring mood, professional landscape photography with rich colors and sharp detail',
    previewImage: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=600&fit=crop',
    gradient: 'from-slate-600 to-blue-600',
    emoji: '⛰️'
  },
  UNDERWATER: {
    id: 'underwater',
    name: 'Underwater',
    description: 'Deep ocean depths',
    prompt: 'an underwater ocean environment with vibrant coral reef formations in various colors (orange, purple, pink), schools of colorful tropical fish swimming around, rays of sunlight penetrating from the surface creating dramatic light beams, floating bubbles and particles, sea plants and anemones swaying, crystal-clear turquoise to deep blue water, peaceful and serene underwater atmosphere, underwater photography style with rich aquatic colors and soft diffused lighting',
    previewImage: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=600&fit=crop',
    gradient: 'from-blue-600 to-teal-600',
    emoji: '🌊'
  },
  CITY: {
    id: 'city',
    name: 'City',
    description: 'Modern urban skyline',
    prompt: 'a modern metropolitan city environment with tall glass and steel skyscrapers, busy urban streets with cars and buses, street lights and building windows glowing, modern architecture with clean lines, blue hour lighting (dusk) with deep blue sky and warm artificial lights, reflective glass surfaces, urban parks or plazas, dynamic and energetic atmosphere, architectural photography style with sharp details and balanced exposure between sky and city lights',
    previewImage: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400&h=600&fit=crop',
    gradient: 'from-gray-600 to-slate-600',
    emoji: '🏙️'
  },
  AURORA: {
    id: 'aurora',
    name: 'Aurora',
    description: 'Northern lights magic',
    prompt: 'an aurora borealis environment with spectacular northern lights dancing across the night sky in vibrant green, purple, and blue colors, snow-covered ground or frozen lake reflecting the lights, silhouettes of pine trees, distant mountains, countless stars visible in the dark sky, ethereal and magical atmosphere, cold crisp winter air, peaceful solitude, long-exposure night photography style with vivid aurora colors and deep starry sky',
    previewImage: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=400&h=600&fit=crop',
    gradient: 'from-green-600 to-blue-600',
    emoji: '🌌'
  },
  NEON: {
    id: 'neon',
    name: 'Neon City',
    description: 'Vibrant neon lights',
    prompt: 'a vibrant neon-lit urban environment with bright colorful neon signs (pink, blue, yellow, green), illuminated storefronts and restaurants, reflective wet pavement creating neon reflections, bustling nightlife atmosphere, modern Asian-inspired street architecture, hanging lanterns and signs with glowing text, warm and cool color contrasts, energetic and lively mood, urban night photography style with high saturation and glowing light effects',
    previewImage: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=400&h=600&fit=crop',
    gradient: 'from-fuchsia-600 to-pink-600',
    emoji: '💫'
  }
};

// Art style transformations for "Full Transformation" mode
export const TRANSFORMATION_STYLES = {
  PAINTED_PORTRAIT: {
    id: 'painted_portrait',
    name: 'Painted Portrait',
    description: 'Bold artistic illustration',
    prompt: 'Vibrant painted portrait illustration style with bold visible brush strokes, rich saturated colors (warm reds, bright oranges, deep blues, vibrant purples), defined color blocks, artistic shading, thick painterly textures. Contemporary illustration style with high color saturation and contrast. Character should be rendered as a stylized painting with expressive brushwork and layered paint effects. Background: Decorative floral or nature elements (flowers, leaves, botanical patterns) in painted style. All elements fully illustrated with visible artistic brush work, no photographic elements. IMPORTANT: No text, logos, or brand names.',
    previewImage: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&h=600&fit=crop',
    gradient: 'from-red-600 to-orange-600',
    emoji: '🎨'
  },
  GHIBLI: {
    id: 'ghibli',
    name: 'Studio Ghibli',
    description: 'Hayao Miyazaki anime style',
    prompt: 'Studio Ghibli animated character style with soft watercolor aesthetics, hand-drawn anime features with large expressive eyes, gentle rounded face, flowing animated hair, soft cel-shaded rendering, warm pastel colors. Character should resemble Spirited Away, My Neighbor Totoro, or Howl\'s Moving Castle characters. Background: Whimsical Ghibli-style environment with soft watercolor landscapes, magical elements, dreamy clouds or nature scenes. All elements fully 2D animated, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=400&h=600&fit=crop',
    gradient: 'from-green-400 to-blue-400',
    emoji: '🎐'
  },
  PIXAR: {
    id: 'pixar',
    name: 'Pixar 3D',
    description: '3D animated character',
    prompt: 'Pixar 3D animated character with smooth stylized CGI rendering, oversized expressive eyes, rounded friendly features, simplified geometry, vibrant saturated colors, glossy smooth skin texture. Character should resemble Up, Toy Story, Inside Out, or Coco style. Background: Vibrant Pixar-style 3D environment with colorful scenery, dramatic lighting, and cinematic composition. Everything rendered as high-quality 3D animation, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400&h=600&fit=crop',
    gradient: 'from-blue-500 to-purple-500',
    emoji: '🎬'
  },
  DISNEY: {
    id: 'disney',
    name: 'Disney Princess',
    description: 'Classic Disney animation',
    prompt: 'Disney princess/prince animated character with elegant 2D hand-drawn features, large expressive eyes with detailed highlights, flowing animated hair with shine, graceful pose, regal fantasy clothing, cel-shaded animation. Style inspired by Frozen, Moana, Tangled, and classic Disney films. Background: Magical Disney castle, enchanted forest, or fairy tale kingdom environment. All elements in classic Disney 2D animation style, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=400&h=600&fit=crop',
    gradient: 'from-pink-400 to-purple-400',
    emoji: '👑'
  },
  ANIME: {
    id: 'anime',
    name: 'Anime',
    description: 'Japanese manga/anime style',
    prompt: 'Modern anime character with sharp clean linework, large vibrant eyes with detailed sparkles and highlights, stylized anime hair with multiple color zones and dramatic shine, cel-shaded skin with soft shading, dynamic expression. Style inspired by popular anime series with manga aesthetics. Background: Anime-style environment with speed lines, gradient skies, or urban Japanese cityscapes. All elements fully illustrated as 2D anime art, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=400&h=600&fit=crop',
    gradient: 'from-red-500 to-pink-500',
    emoji: '⚡'
  },
  COMIC: {
    id: 'comic',
    name: 'Comic Book',
    description: 'Superhero comic style',
    prompt: 'Comic book superhero illustration with bold black ink outlines, halftone dot shading, dramatic high-contrast lighting, heroic features, vibrant primary colors (red, blue, yellow). Hand-drawn comic art with Marvel/DC aesthetics, dynamic action pose, muscular proportions. Background: Comic book city skyline, action effects (POW!, BOOM!), or dramatic comic panels. All elements illustrated as traditional comic book art with ink lines and color fills, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=600&fit=crop',
    gradient: 'from-red-600 to-blue-600',
    emoji: '💥'
  },
  CLAYMATION: {
    id: 'claymation',
    name: 'Claymation',
    description: 'Stop-motion clay style',
    prompt: 'Claymation stop-motion character with handmade clay texture, slightly imperfect sculpted feel, chunky rounded features, matte surface with subtle fingerprint marks, warm earthy colors. Character should resemble Wallace and Gromit, Chicken Run, or Shaun the Sheep style. Background: Miniature claymation set with handcrafted props and scenery. Everything rendered as tactile clay models in stop-motion style, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=400&h=600&fit=crop',
    gradient: 'from-orange-500 to-brown-500',
    emoji: '🎭'
  },
  WATERCOLOR: {
    id: 'watercolor',
    name: 'Watercolor',
    description: 'Painted portrait style',
    prompt: 'Watercolor painting portrait with soft flowing translucent colors, visible brush strokes, gentle color bleeding and wet-on-wet blending, pastel color palette (soft pinks, blues, purples), textured watercolor paper effect, dreamy ethereal quality. Fine art watercolor portrait with artistic interpretation and loose flowing style. Background: Abstract watercolor washes, floral elements, or dreamy landscapes. All elements rendered as hand-painted watercolor artwork with visible brush marks, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=600&fit=crop',
    gradient: 'from-purple-300 to-pink-300',
    emoji: '🎨'
  },
  FANTASY_RPG: {
    id: 'fantasy_rpg',
    name: 'Fantasy RPG',
    description: 'Epic fantasy character',
    prompt: 'Fantasy RPG character design with epic heroic features, detailed medieval armor with ornate engravings OR flowing mystical robes with magical runes, fantasy elements (elf ears, magical aura, glowing eyes), dramatic lighting with rim lights, concept art illustration style. Inspired by Dungeons & Dragons, Elder Scrolls, World of Warcraft character art. Background: Epic fantasy landscape with castles, mountains, magical portals, or mystical forests. All elements rendered as high-quality fantasy concept art illustration, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&h=600&fit=crop',
    gradient: 'from-purple-600 to-indigo-600',
    emoji: '⚔️'
  },
  POP_ART: {
    id: 'pop_art',
    name: 'Pop Art',
    description: 'Andy Warhol style',
    prompt: 'Pop art illustration with bold flat vibrant colors, high contrast, halftone dot patterns for shading, thick black outlines, limited color palette (3-4 bold colors), posterized effect with simplified color zones. Andy Warhol screen print style with 1960s pop culture aesthetic and graphic simplification. Background: Bold solid color or repeated patterns in pop art style. All elements illustrated as flat graphic pop art print, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=600&fit=crop',
    gradient: 'from-yellow-400 to-pink-500',
    emoji: '🖼️'
  },
  RENAISSANCE: {
    id: 'renaissance',
    name: 'Renaissance',
    description: 'Classical oil painting',
    prompt: 'Renaissance oil painting portrait with classical fine art techniques, soft chiaroscuro lighting (dramatic light and shadow), rich layered oil paint colors (deep burgundy, gold, emerald), smooth blended brushwork, visible canvas texture, regal period clothing (velvet, silk, jewels), noble pose. Inspired by Leonardo da Vinci, Raphael, Titian masterworks. Background: Classical architecture, draped fabrics, or dark atmospheric backgrounds. All elements rendered as museum-quality oil painting with traditional techniques, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=600&fit=crop',
    gradient: 'from-amber-600 to-brown-600',
    emoji: '🖌️'
  },
  CYBERPUNK_CHAR: {
    id: 'cyberpunk_char',
    name: 'Cyberpunk',
    description: 'Futuristic cyber character',
    prompt: 'Cyberpunk character design with futuristic tech implants (glowing eyes, facial circuits), neon hair highlights (cyan, magenta, electric blue), edgy tactical clothing with LED accents and holographic elements, cybernetic enhancements visible, glowing technological elements, dystopian punk aesthetic. Inspired by Cyberpunk 2077, Blade Runner, Ghost in the Shell. Background: Neon-lit futuristic city with holographic advertisements, rain-slicked streets, flying vehicles. All elements rendered as high-tech digital illustration, no photographic elements.',
    previewImage: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=600&fit=crop',
    gradient: 'from-cyan-500 to-purple-600',
    emoji: '🤖'
  },
  VINTAGE: {
    id: 'vintage',
    name: 'Vintage Photo',
    description: 'Classic 1950s portrait',
    prompt: 'Vintage 1950s glamour portrait with classic Hollywood style, elegant retro hairstyle (victory rolls, pompadour), period-appropriate clothing and accessories, timeless elegant pose, dramatic studio lighting. Black and white OR warm sepia tones with film grain texture, soft vignette, slightly faded colors if colored. Background: Classic photography studio backdrop or vintage interior. All elements rendered as authentic vintage photograph aesthetic, maintaining photographic quality with retro processing.',
    previewImage: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7464?w=400&h=600&fit=crop',
    gradient: 'from-gray-500 to-amber-600',
    emoji: '📷'
  }
};

// Legacy STYLES object for backwards compatibility - defaults to environment styles
export const STYLES = ENVIRONMENT_STYLES;

// Timing constants
export const COUNTDOWN_DURATION = 3; // seconds
export const AUTO_RESET_TIMEOUT = 60000; // 60 seconds

// Loading messages for generating images
export const GENERATING_MESSAGES = [
  'Creating magic...',
  'Transforming your style...',
  'Memory in the making...',
  'Adding some creativity...',
  'Almost there...'
];

// Loading messages for saving images
export const SAVING_MESSAGES = [
  'Preparing your photo...',
  'Uploading to cloud...',
  'Generating QR code...',
  'Almost ready...',
  'Finalizing...'
];

// Legacy export for backwards compatibility
export const LOADING_MESSAGES = GENERATING_MESSAGES;

// Firebase collection path
export const IMAGES_COLLECTION = 'generated_images';

// TTL duration (24 hours in milliseconds)
export const IMAGE_TTL = 24 * 60 * 60 * 1000;
